import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import api from '../services/api';
import useDefaultTradingMode from '../hooks/useDefaultTradingMode';
import { CHAIN_LABELS, STRATEGY_LABELS, formatTime } from '../utils/masterChefUtils';

const SEVERITY_STYLES = {
  good: 'border-green-500 bg-green-50 dark:bg-green-900/20',
  warn: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
  info: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
};
const SEVERITY_ICON = { good: '✅', warn: '⚠️', info: '💡' };

const MODES = [
  { id: 'all', label: 'All Trades' },
  { id: 'live', label: 'Live Only' },
  { id: 'dry_run', label: 'Dry Run Only' },
];

function pct(n) {
  if (n == null) return '—';
  return `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`;
}

function winColor(rate) {
  if (rate >= 55) return 'text-green-600 dark:text-green-400';
  if (rate >= 40) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function HeadlineCard({ label, value, sub, valueClass = 'text-gray-900 dark:text-white' }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

/** Breakdown table sorted by a metric. keyLabel maps raw key → display. */
function BreakdownCard({
  title, data, keyLabel, subtitle, sortKey = 'winRate', bestKey, worstKey, showAggRoi = false,
}) {
  const rows = Object.entries(data || {})
    .map(([k, s]) => ({ key: k, ...s }))
    .filter((r) => r.decided > 0)
    .sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));

  if (!rows.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
        <p className="text-xs text-gray-400 py-4 text-center">No closed trades yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
        {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700">
            <th className="text-left px-3 py-1.5">{keyLabel}</th>
            <th className="text-right px-2 py-1.5">Trades</th>
            <th className="text-right px-2 py-1.5">Win %</th>
            <th className="text-right px-2 py-1.5">Avg P&L</th>
            {showAggRoi && <th className="text-right px-2 py-1.5">$ ROI</th>}
            <th className="text-right px-3 py-1.5">PF</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isBest = bestKey && r.key === bestKey;
            const isWorst = worstKey && r.key === worstKey;
            const rowClass = isBest
              ? 'bg-green-50 dark:bg-green-900/20'
              : isWorst
                ? 'bg-red-50 dark:bg-red-900/20'
                : '';
            return (
              <tr key={r.key} className={`border-b dark:border-gray-700 last:border-0 ${rowClass}`}>
                <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-white">
                  {r.key}
                  {isBest && <span className="ml-1 text-[9px] text-green-600 dark:text-green-400">best</span>}
                  {isWorst && <span className="ml-1 text-[9px] text-red-600 dark:text-red-400">worst</span>}
                </td>
                <td className="px-2 py-1.5 text-right text-gray-600 dark:text-gray-300">
                  {r.decided}{r.open ? <span className="text-gray-400"> +{r.open}</span> : ''}
                </td>
                <td className={`px-2 py-1.5 text-right font-semibold ${winColor(r.winRate)}`}>{r.winRate}%</td>
                <td className={`px-2 py-1.5 text-right font-semibold ${r.avgPnlPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pct(r.avgPnlPct)}</td>
                {showAggRoi && (
                  <td className={`px-2 py-1.5 text-right ${r.aggRoiPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pct(r.aggRoiPct)}</td>
                )}
                <td className="px-3 py-1.5 text-right text-gray-500">{r.profitFactor == null ? '∞' : r.profitFactor}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TradeList({ title, trades, emptyMsg, valueKey = 'pnlPct', extraNote }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
        {extraNote && <p className="text-[10px] text-gray-400 mt-0.5">{extraNote}</p>}
      </div>
      {!trades || trades.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center">{emptyMsg}</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {trades.map((t) => (
            <div key={t.id} className="flex items-center gap-2 px-4 py-2 text-xs">
              <span className="font-medium text-gray-900 dark:text-white w-20 truncate">{t.symbol}</span>
              <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {CHAIN_LABELS[t.chain] || t.chain}
              </span>
              <span className="text-[9px] text-gray-400">{STRATEGY_LABELS[t.strategy] || t.strategy}</span>
              {t.peakMultiple != null && (
                <span className="text-[10px] text-purple-500 dark:text-purple-400">peak {t.peakMultiple}x</span>
              )}
              <span className={`ml-auto font-bold ${(t[valueKey] || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {valueKey === 'currentMultiple' ? `${t.currentMultiple}x` : pct(t[valueKey])}
              </span>
              {t.exit && <span className="text-[9px] text-gray-400 w-16 text-right">{t.exit.replace('_', ' ')}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WinRateBars({ data, title, note }) {
  const rows = Object.entries(data || {})
    .map(([k, s]) => ({ name: k, winRate: s.winRate, decided: s.decided }))
    .filter((r) => r.decided > 0);

  // numeric-ish sort for hours, else by name
  rows.sort((a, b) => {
    const an = parseInt(a.name, 10);
    const bn = parseInt(b.name, 10);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return a.name.localeCompare(b.name);
  });

  if (!rows.length) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
      {note && <p className="text-[10px] text-gray-400 mb-2">{note}</p>}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={rows} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8 }}
            formatter={(v, n, p) => [`${v}% (${p.payload.decided} trades)`, 'Win rate']}
          />
          <Bar dataKey="winRate" radius={[3, 3, 0, 0]}>
            {rows.map((r) => (
              <Cell key={r.name} fill={r.winRate >= 55 ? '#16a34a' : r.winRate >= 40 ? '#ea580c' : '#dc2626'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PeakDistribution({ peakDistribution, total }) {
  if (!peakDistribution?.length) return null;
  const rows = peakDistribution.map((d) => ({
    name: `${d.multiple}x+`,
    count: d.count,
    rate: total ? +(d.count / total * 100).toFixed(0) : 0,
  }));
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Peak Multiple Reached</h3>
      <p className="text-[10px] text-gray-400 mb-2">How many positions ran to each multiple at their peak (max favorable excursion)</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={rows} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8 }}
            formatter={(v, n, p) => [`${v} positions (${p.payload.rate}%)`, 'Reached']}
          />
          <Bar dataKey="count" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MasterChefDataset() {
  const [mode, setMode] = useDefaultTradingMode('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/masterchef/dataset?mode=${mode}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch dataset:', err);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return <div className="text-center py-12 text-gray-500">Analyzing Master Chef trades…</div>;
  }

  const o = data?.overall || {};
  const b = data?.breakdowns || {};
  const m = data?.missing || {};
  const mc = data?.entryMcSummary || {};
  const bestMc = mc.best;
  const worstMc = mc.worst;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">🍳 Master Chef Dataset</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Pattern analysis & win-rate intelligence across {data?.totalPositions || 0} positions
            {data?.generatedAt && ` · updated ${formatTime(data.generatedAt)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            {MODES.map((mm) => (
              <button
                key={mm.id}
                onClick={() => setMode(mm.id)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  mode === mm.id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium shadow' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {mm.label}
              </button>
            ))}
          </div>
          <button onClick={fetchData} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeadlineCard label="Closed Trades" value={o.decided || 0} sub={`${o.open || 0} still open`} />
        <HeadlineCard
          label="Win Rate" value={`${o.winRate || 0}%`}
          sub={`${o.wins || 0}W / ${o.losses || 0}L`}
          valueClass={winColor(o.winRate || 0)}
        />
        <HeadlineCard
          label="Avg P&L / Trade" value={pct(o.avgPnlPct)}
          valueClass={(o.avgPnlPct || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
          sub="equal-weighted"
        />
        <HeadlineCard
          label="Avg Win" value={pct(o.avgWinPct)} valueClass="text-green-600 dark:text-green-400"
        />
        <HeadlineCard
          label="Avg Loss" value={pct(o.avgLossPct)} valueClass="text-red-600 dark:text-red-400"
        />
        <HeadlineCard
          label="Profit Factor" value={o.profitFactor == null ? '∞' : (o.profitFactor || 0)}
          sub="gross win ÷ gross loss"
          valueClass={(o.profitFactor == null || o.profitFactor >= 1.5) ? 'text-green-600 dark:text-green-400' : o.profitFactor >= 1 ? 'text-orange-600' : 'text-red-600'}
        />
      </div>

      {/* Entry MC profitability highlight */}
      {(bestMc || worstMc) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <HeadlineCard
            label="Most Profitable MC Range"
            value={bestMc ? bestMc.band : '—'}
            sub={bestMc ? `${pct(bestMc.avgPnlPct)} avg · ${bestMc.winRate}% win · ${bestMc.decided} closed trades · ${pct(bestMc.aggRoiPct)} $ ROI` : undefined}
            valueClass="text-green-600 dark:text-green-400"
          />
          <HeadlineCard
            label="Worst MC Range"
            value={worstMc ? worstMc.band : '—'}
            sub={worstMc ? `${pct(worstMc.avgPnlPct)} avg · ${worstMc.winRate}% win · ${worstMc.decided} closed trades · ${pct(worstMc.aggRoiPct)} $ ROI` : undefined}
            valueClass="text-red-600 dark:text-red-400"
          />
        </div>
      )}

      {/* Recommendations */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">🎯 How to improve the win rate</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {(data?.recommendations || []).map((r, i) => (
            <div key={i} className={`rounded-lg border-l-4 p-3 ${SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.info}`}>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {SEVERITY_ICON[r.severity] || '💡'} {r.title}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{r.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BreakdownCard title="By Strategy" data={b.byStrategy} keyLabel="Strategy" subtitle="Win rate & avg P&L per entry type" />
        <BreakdownCard title="By Chain" data={b.byChain} keyLabel="Chain" subtitle="Which chain produces the best trades" />
        <BreakdownCard title="By Exit Type" data={b.byExitType} keyLabel="Exit" subtitle="How positions are being closed" />
        <BreakdownCard title="By Hold Duration" data={b.byHold} keyLabel="Held" subtitle="Sweet spot for how long to hold" />
        <BreakdownCard
          title="By Entry Market Cap"
          data={b.byEntryMc}
          keyLabel="MC Band"
          subtitle="Sorted by avg P&L — green = most profitable, red = worst"
          sortKey="avgPnlPct"
          bestKey={bestMc?.band}
          worstKey={worstMc?.band}
          showAggRoi
        />
        <BreakdownCard title="By Day of Week" data={b.byDay} keyLabel="Day" subtitle="UTC, by open time" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <WinRateBars data={b.byHour} title="Win Rate by Hour (UTC)" note="Concentrate entries in the green hours" />
        <PeakDistribution peakDistribution={data?.peakDistribution} total={data?.totalPositions} />
      </div>

      {/* What we're missing */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-2">🔍 What we're missing / leaving on the table</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
          <HeadlineCard
            label="Ran 2x+ then lost" value={m.missedRunnersCount || 0}
            sub="winners that reversed into losses" valueClass="text-orange-600 dark:text-orange-400"
          />
          <HeadlineCard
            label="Sold too early" value={m.soldEarlyCount || 0}
            sub="peaked 3x+ but took <60%" valueClass="text-orange-600 dark:text-orange-400"
          />
          <HeadlineCard
            label="Stop-loss rate" value={`${m.stopLossStats?.rate || 0}%`}
            sub={`avg ${pct(m.stopLossStats?.avgLossPct)} · ${m.stopLossStats?.recoveredAfterSl || 0} later ran 1.5x+`}
            valueClass="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <TradeList
            title="Missed Runners" trades={m.missedRunners}
            emptyMsg="None — winners are being locked in 👍"
            extraNote="Peaked 2x+ but closed at a loss"
          />
          <TradeList
            title="Sold Too Early" trades={m.soldEarly}
            emptyMsg="None — riding winners well 👍"
            extraNote="Peaked 3x+ but realized under 60%"
          />
          <TradeList
            title="Deep Underwater (open)" trades={m.deepUnderwater} valueKey="currentMultiple"
            emptyMsg="No open positions deeply down"
            extraNote="Live/active positions under 0.7x"
          />
        </div>
      </div>

      {/* Best / worst */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TradeList title="🏆 Best Trades" trades={data?.bestTrades} emptyMsg="No closed trades yet" />
        <TradeList title="💀 Worst Trades" trades={data?.worstTrades} emptyMsg="No closed trades yet" />
      </div>
    </div>
  );
}
